import classnames from 'classnames';
import { useMemo, type CSSProperties } from 'react';

import { PriceDeprecated } from '@/core/tamagoshi/deprecated/PriceDeprecated/PriceDeprecated';
import {
  DeliveryLabel,
  type DeliveryLabelType,
} from '@/core/tamagoshi/ProductCard/components/DeliveryLabel/DeliveryLabel';
import { Text } from '@/core/tamagoshi/Text/Text';
import { Image } from '@/domains/core/Image/Image';
import { Link } from '@/domains/core/routing/components/Link';
import { isB2B, isUkPlatform } from '@/domains/core/settings/utils';
import { Rating as TamagoshiRating } from '@/domains/core/tamagoshiTailwind/components/Rating/Rating';
import { rating as ratingTranslation } from '@/domains/core/tamagoshiTailwind/translations';
import { generateUrl } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/AlgoliaProductCard.utils';
import { isMobile } from '@/domains/productDiscovery/HeaderLeonidas/utils/isMobile';
import {
  getProductCardTranslations,
  translations,
} from '@/domains/productDiscovery/ProductCard/translations';
import { useProductBundle } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/context/ProductBundle/ProductBundle.context';
import type { IProduct } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/context/ProductBundle/productBundle.interfaces';
import { ECardCurrency } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/ProductBundle.interfaces';
import {
  allTaxExcludedShort,
  allTaxIncludedShort,
} from '@/shoppingCart/CartPage/components/ProductCard/ProductPrices/translations';

import styles from './outlineBundleProductCard.module.scss';

const OutlineBundleProductCard = ({
  modelId,
  imageFullpath,
  title,
  url,
  articleId,
  modelsCount,
  bundlePrices,
  isInBundle,
  delivery,
  isMainProduct,
  rating,
  ratingCount,
}: IProduct & {
  delivery?: DeliveryLabelType;
} & { isMainProduct: boolean }) => {
  const { handleProductCardClick } = useProductBundle();
  const isMobileUser = isMobile();
  const numberOfItem = +!!delivery + +!!rating;
  const lineClamp = useMemo(() => {
    const baseValue = isB2B() ? 3 : 4;
    return isMobileUser ? baseValue - numberOfItem : baseValue;
  }, [numberOfItem, isMobileUser]);

  return (
    <div
      style={
        {
          '--productCardLineClamp': lineClamp,
        } as CSSProperties
      }
      className={styles.wrapper}
      data-testid="outline-bundle-product-card"
    >
      <div className={styles.imageWrapper}>
        <Link
          className={classnames(
            styles.imageLink,
            isMainProduct && styles.imageMainProductLink,
          )}
          href={`p/${generateUrl({
            url,
            modelId,
            articleId,
            modelsCount,
          })}`}
          onClick={(e) => isMainProduct && e.preventDefault()}
          data-testid="outline-bundle-product-card-image-link"
        >
          <Image
            data-testid="outline-bundle-product-card-image"
            className={classnames(
              !isInBundle && styles.imageIsUnchecked,
              styles.bundleImage,
            )}
            src={imageFullpath}
            alt={title}
            width="100%"
            height="100%"
          />
        </Link>
      </div>
      <div className={styles.infoWrapper}>
        {isMainProduct ? (
          <p
            className={styles.title}
            data-testid="outline-bundle-product-card-title"
          >
            {title}
          </p>
        ) : (
          <>
            <Link
              data-testid="outline-bundle-product-card-link"
              className={classnames(
                styles.title,
                styles.titleIsLink,
                !isInBundle && styles.isUnchecked,
              )}
              onClick={() => handleProductCardClick(articleId)}
              href={`p/${generateUrl({
                url,
                modelId,
                articleId,
                modelsCount,
              })}`}
            >
              {title}
            </Link>
          </>
        )}
        <TamagoshiRating
          value={rating}
          size="S"
          aria-label={ratingTranslation.ariaLabel}
          reviewsCount={ratingCount}
        />
        <div className={styles.priceWrapper}>
          <PriceDeprecated
            className={classnames(
              styles.price,
              !isInBundle && styles.isUnchecked,
            )}
            value={bundlePrices.main}
            currency={isUkPlatform() ? ECardCurrency.GB : ECardCurrency.EU}
            translations={
              isB2B() ? getProductCardTranslations(0).price : undefined
            }
            supDecimal
          />
          {isB2B() && (
            <Text tag="p" className={styles.priceVat}>
              {bundlePrices.taxKey === 'with_vat'
                ? allTaxIncludedShort
                : allTaxExcludedShort}
            </Text>
          )}
        </div>
        <DeliveryLabel
          translations={translations.deliveryLabel}
          deliveryLabel={delivery}
          className={classnames(!isInBundle && styles.isUnchecked)}
        />
      </div>
    </div>
  );
};

OutlineBundleProductCard.displayName = 'OutlineBundleProductCard';

export default OutlineBundleProductCard;
